import React from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";

export type StatusType = "idle" | "saving" | "saved" | "error" | "creating" | "deleting";

interface StatusIndicatorProps {
  status: StatusType;
  children?: React.ReactNode;
  className?: string;
  /**
   * true にすると `position: fixed` ではなく親要素のフロー内で表示する。
   * リスト直上などに置きたい場合に使用する。
   */
  inline?: boolean;
  /**
   * true にすると、最近接の `position: relative/absolute/fixed` 親（通常は
   * Modal の Content）の右下 (bottom: 8px / right: 8px) に絶対配置する。
   * ビューポート右下の `position: fixed` ではない点が `inline` でも `default`
   * でもないところ。
   */
  inModal?: boolean;
  /**
   * BottomAppBar の表示/非表示に追従して縦位置を変える挙動を有効にするか。
   * BottomAppBar が存在しない画面（マイページ等）では `false` にして
   * スクロールで上下に動かないようにする。既定: `true`。
   */
  withBottomAppBar?: boolean;
}

const getStatusColor = (status: StatusType) => {
  switch (status) {
    case "saving":
    case "creating":
    case "deleting":
      return "#a7a8a9";
    case "saved":
      return "var(--soremo-blue)";
    case "error":
      return "var(--soremo-green)";
    case "idle":
    default:
      return "transparent";
  }
};

const StyledStatusIndicator = styled.div<{ $status: StatusType }>`
  font-size: 8px;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${(props) => {
    switch (props.$status) {
      case "saving":
      case "creating":
      case "deleting":
        return "#fcfcfc";
      case "saved":
      case "error":
        return "#fff";
      case "idle":
      default:
        return "transparent";
    }
  }};
  white-space: nowrap;

  &&&& {
    color: ${(props) => getStatusColor(props.$status)};
    border: 1px solid ${(props) => getStatusColor(props.$status)};
  }
`;

const StatusContainer = styled.div<{ $isVisible: boolean; $bottom: number }>`
  position: fixed;
  bottom: ${(props) => props.$bottom}px;
  right: 24px;
  z-index: 1000;
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};
  pointer-events: ${(props) => (props.$isVisible ? "auto" : "none")};
  transition: opacity 0.3s ease, bottom 0.15s linear;
`;

const InlineContainer = styled.div<{ $isVisible: boolean }>`
  display: inline-flex;
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};
  pointer-events: ${(props) => (props.$isVisible ? "auto" : "none")};
  transition: opacity 0.3s ease;
`;

// 最近接の position: relative/absolute/fixed 親を基準にした右下固定
const ModalAnchoredContainer = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 5;
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};
  pointer-events: ${(props) => (props.$isVisible ? "auto" : "none")};
  transition: opacity 0.3s ease;
`;

const DEFAULT_MESSAGES: Record<StatusType, string> = {
  idle: "",
  saving: "保存中...",
  saved: "保存済み",
  error: "保存に失敗しました",
  creating: "作成中...",
  deleting: "削除中...",
};

const BOTTOM_APP_BAR_HEIGHT = 80;
const BASE_BOTTOM = 24;

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  children,
  className,
  inline = false,
  inModal = false,
  withBottomAppBar = true,
}) => {
  const scrollState = useSelector((state: { layout: string }) => state.layout);
  const isVisible = status !== "idle";
  const isBarVisible = scrollState !== "down";
  const bottom = withBottomAppBar
    ? isBarVisible
      ? BASE_BOTTOM + BOTTOM_APP_BAR_HEIGHT
      : BASE_BOTTOM
    : BASE_BOTTOM;

  if (inModal) {
    return (
      <ModalAnchoredContainer $isVisible={isVisible}>
        <StyledStatusIndicator $status={status} className={className}>
          {children || DEFAULT_MESSAGES[status]}
        </StyledStatusIndicator>
      </ModalAnchoredContainer>
    );
  }

  if (inline) {
    return (
      <InlineContainer $isVisible={isVisible}>
        <StyledStatusIndicator $status={status} className={className}>
          {children || DEFAULT_MESSAGES[status]}
        </StyledStatusIndicator>
      </InlineContainer>
    );
  }

  return (
    <StatusContainer $isVisible={isVisible} $bottom={bottom}>
      <StyledStatusIndicator $status={status} className={className}>
        {children || DEFAULT_MESSAGES[status]}
      </StyledStatusIndicator>
    </StatusContainer>
  );
};

export default StatusIndicator;